import styles from './template.css';
import template from './template';
import {AoflElement, customElement, property} from '@aofl/element';
import {storeInstance} from '@aofl/store/dist/legacy';
import {productLabels, productStatuses, sdoNs} from '@modules/constants-enumerate';
import {ApiService} from '@modules/api-service';
import {i18nMixin} from '@aofl/i18n-mixin';
import translations from '@modules/i18n';
import {until} from 'lit-html/directives/until';

const setTranslations = (ctx) => {
  return {
    labels: {
      selectProduct: ctx.__('<tt-mL1qFakA>', 'Select Product'),
      selectLicense: ctx.__('<tt-GeR0J7jg>', 'Select License'),
    }
  };
};

/**
 * @summary LicenseFilters
 * @extends {AoflElement}
 */
@customElement('license-filters')
class LicenseFilters extends i18nMixin(AoflElement) {
  /**
   * Creates an instance of LicenseFilters.
   */
  constructor() {
    super();
    this.translations = translations;
  }

  /**
   * @readonly
   */
  static is = 'license-filters';

  @property({type: String})
  licenseStatus = '';

  @property({type: Number})
  licenseStatusIndex = 0;

  @property({type: Array})
  licenseStatuses = [];

  @property({type: String})
  currentLicenseStatusLabel = null;

  @property({type: Number})
  license = '';

  @property({type: Number})
  licenseIndex = 0;

  @property({type: Array})
  licenseOptions = [];

  @property({type: String})
  currentLicenseLabel = null;

  @property({type: String})
  product = '';

  @property({type: Number})
  productIndex = 0;

  @property({type: Array})
  productOptions = [];

  @property({type: String})
  currentProductLabel = null;

  @property({type: Boolean})
  showLicenses = null;

  @property({type: Boolean})
  showLicensesFromRoot = null;

  @property({type: Array})
  licenseResults = null;

  buildLicenseDropdownOptions() {
    const temp = [
      {
        label: until(this.t10s.labels.selectLicense),
        value: '',
        product: ''
      }
    ];
    const productsForThisGroup = [];

    for (let i = 0; i < this.licenseResults.results.length; i++) {
      if (this.licenseResults.results[i].product === this.product || this.product === '') {
        temp.push({
          label: this.licenseResults.results[i].name,
          value: this.licenseResults.results[i].id,
          product: this.licenseResults.results[i].product
        });
      }
      productsForThisGroup[this.licenseResults.results[i].product] = true;
    }
    this.licenseOptions = temp; // this is set because pushing individual values will not cause an update

    // set license value to blank in case the currently selected license does not match the current Product license list
    this.currentLicenseLabel = this.licenseOptions[0].label;
    this.license = '';

    return productsForThisGroup;
  }

  setLicenseStatusToAssignedInvitedRedeemed() {
    if (this.licenseStatusIndex === 0) {
      for (let i = 0; i < this.licenseStatuses.length; i++) {
        if (this.licenseStatuses[i].value === productStatuses.ASSIGNED_INVITED_REDEEMED) {
          this.licenseStatusIndex = i;
          this.licenseStatus = this.licenseStatuses[i].value;
          this.currentLicenseStatusLabel = this.licenseStatuses[i].label;
        }
      }
    }
  }

  selectOption(source, index) {
    if (source === 'aofl_product') {
      this.productIndex = index;
      this.product = this.productOptions[index].value;
      this.currentProductLabel = this.productOptions[index].label;

      // Selecting product reduces the options under license name to only show licenses for that product
      this.buildLicenseDropdownOptions();

      // Selecting product sets the status filter to "assigned + invited + redeemed" if status does not already have a value
      this.setLicenseStatusToAssignedInvitedRedeemed();
    } else if (source === 'license_id') {
      this.licenseIndex = index;
      this.license = this.licenseOptions[index].value;
      this.currentLicenseLabel = this.licenseOptions[index].label;

      // Selecting product reduces the options under license name to only show licenses for that product
      for (let i = 0; i < this.productOptions.length; i++) {
        if (this.productOptions[i].value === this.licenseOptions[index].product) {
          this.productIndex = i;
          this.product = this.productOptions[i].value;
          this.currentProductLabel = this.productOptions[i].label;
        }
      }

      // Selecting license sets the status filter to "assigned + invited + redeemed" if status does not already have a value
      this.setLicenseStatusToAssignedInvitedRedeemed();
    } else if (source === 'license_status') {
      this.licenseStatusIndex = index;
      this.licenseStatus = this.licenseStatuses[index].value;
      this.currentLicenseStatusLabel = this.licenseStatuses[index].label;
    }

    this.dispatchEvent(new CustomEvent('license-status-change', {
      composed: true,
      bubbles: true
    }));
  }

  async connectedCallback() {
    super.connectedCallback();

    // Retrieve product and license data for this group
    const state = storeInstance.getState();
    const filters = {};
    filters.group_path = state[sdoNs.SESSION].group_info.path + state[sdoNs.SESSION].group_info.id + '.';
    filters.hide_expired = true;
    filters.show_licenses_from_root = this.showLicensesFromRoot;
    this.licenseResults = await ApiService.licenseSearch([], filters);

    this.t10s = setTranslations(this);

    // Generate dropdown options for licenses
    const productsForThisGroup = this.buildLicenseDropdownOptions();

    // Generate dropdown options for products
    const temp = [
      {
        label: until(this.t10s.labels.selectProduct),
        value: ''
      }
    ];
    for (const productOption in productsForThisGroup) {
      temp.push({
        label: productLabels[productOption],
        value: productOption
      });
    }
    this.productOptions = temp; // this is set because pushing individual values will not cause an update, might need to sort this

    // Set dropdowns to any values passed in
    this.currentProductLabel = this.productOptions[0].label;
    this.productOptions.forEach(function(item, index) {
      if (item.value === this.product) {
        this.productIndex = index;
        this.currentProductLabel = item.label;
      }
    }, this);

    this.currentLicenseLabel = this.licenseOptions[0].label;
    this.licenseOptions.forEach(function(item, index) {
      if (item.value === this.license) {
        this.licenseIndex = index;
        this.currentLicenseLabel = item.label;
      }
    }, this);

    this.currentLicenseStatusLabel = this.licenseStatuses[0].label;
    this.licenseStatuses.forEach(function(item, index) {
      if (item.value === this.licenseStatus) {
        this.licenseStatusIndex = index;
        this.currentLicenseStatusLabel = item.label;
      }
    }, this);
  }


  /**
   *
   * @return {Object}
   */
  render() {
    return super.render({
      default: {
        template,
        styles: [styles]
      }
    });
  }
}

export default LicenseFilters;
